/**
 * Eigenbau: Gemeinsame Hülle der Einstellungen — einheitliche Navigation und
 * Meldungszeilen über alle Bereiche hinweg.
 *
 * Die Einstellungen waren zuvor auf eine flache Reiterleiste mit gemischten
 * Bereichen verteilt (persönlich neben Instanz, Betriebswerkzeuge neben Listen).
 * Diese Hülle trennt die Bereiche klar und hält Bauform und Navigation an
 * einer Stelle.
 */

import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Titel } from "@/components/entwurf/Titel";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { cx } from "@/lib/utils";
import { grundVon, istNetzfehler } from "@/lib/api";
import { meldungZu, type Meldung } from "@/lib/meldung";

export type EinstellungsBereich =
  | "persoenlich"
  | "zeitplanung"
  | "sichten"
  | "team"
  | "instanz"
  | "metarr"
  | "rezepte"
  | "zugaenge"
  | "gitlab";

export const EINSTELLUNGS_LINKS: readonly {
  id: EinstellungsBereich;
  label: string;
  to: string;
  beschreibung: string;
}[] = [
  {
    id: "persoenlich",
    label: "Persönlich",
    to: "/einstellungen/persoenlich",
    beschreibung: "Zeitzone und Tageszusammenfassung.",
  },
  {
    id: "zeitplanung",
    label: "Zeitplanung",
    to: "/einstellungen/zeitplanung",
    beschreibung: "Kapazität, Abwesenheiten und Kalender-Abonnement.",
  },
  {
    id: "sichten",
    label: "Sichten",
    to: "/einstellungen/sichten",
    beschreibung: "Gespeicherte Aufgabenfilter.",
  },
  {
    id: "team",
    label: "Team",
    to: "/einstellungen/team",
    beschreibung: "Personen und Gruppen.",
  },
  {
    id: "instanz",
    label: "Instanz",
    to: "/einstellungen/instanz",
    beschreibung: "Grenzen und projektweite Abläufe.",
  },
  {
    id: "metarr",
    label: "Metarr",
    to: "/einstellungen/metarr",
    beschreibung: "KI-Anbieter, Modellrouting und Hausmeister.",
  },
  {
    id: "rezepte",
    label: "Rezepte",
    to: "/einstellungen/rezepte",
    beschreibung: "Prüfvorschriften für Durchsicht und Auto Q&A.",
  },
  {
    id: "zugaenge",
    label: "Zugänge",
    to: "/einstellungen/zugaenge",
    beschreibung: "Zugänge für Claude Code, Codex und andere MCP-Clients.",
  },
  {
    id: "gitlab",
    label: "GitLab",
    to: "/einstellungen/gitlab",
    beschreibung: "Daten eines Projekts nachziehen.",
  },
] as const;

export function EinstellungenNavigation({
  aktiv,
}: {
  aktiv: EinstellungsBereich;
}) {
  return (
    /*
      Die Marke sitzt **auf** der Linie, nicht darunter.

      Der Verweis war ein Inline-Element. Bei einem solchen zählt
      `padding-bottom` nicht für die Zeilenbox — der 2px-Schatten wurde zwölf
      Pixel unterhalb der `border-b` des Rahmens gezeichnet und hing frei in
      der Luft. `block` macht den Verweis zur eigenen Box, damit die Polsterung
      trägt; `-mb-px` zieht die Marke die eine Linienstärke nach unten, sodass
      sie die Tintenlinie überdeckt statt sie zu doppeln.

      Der Entwurf schreibt genau das:
      `padding-bottom: 12px; margin-bottom: -1px; box-shadow: inset 0 -2px 0 0 var(--tabmarke)`
      bei 12.5px Mono — der aktive Reiter trägt den Versatz, die ruhenden nicht.
    */
    <nav aria-label="Einstellungen" className="border-b border-tinte">
      <ul className="flex flex-wrap items-center gap-[22px]">
        {EINSTELLUNGS_LINKS.map((eintrag) => (
          <li key={eintrag.id} className="flex">
            <Link
              to={eintrag.to}
              className={cx(
                "block pb-3 font-mono text-[12.5px] transition-colors duration-150 ease-aus",
                aktiv === eintrag.id
                  ? "-mb-px font-medium text-tinte shadow-[inset_0_-2px_0_0_var(--tabmarke)]"
                  : "text-tinte4 hover:text-tinte",
              )}
            >
              {eintrag.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function EinstellungenHuelle({
  aktiv,
  beschreibung,
  children,
}: {
  aktiv: EinstellungsBereich;
  beschreibung?: string;
  children: ReactNode;
}) {
  const bereich = EINSTELLUNGS_LINKS.find((e) => e.id === aktiv);
  return (
    <div className="space-y-7">
      <div>
        <Titel grad={46}>Einstellungen</Titel>
        <p className="mt-2 max-w-[62ch] text-[15.5px] font-light leading-[1.7] text-tinte2">
          {beschreibung ?? bereich?.beschreibung ?? ""}
        </p>
      </div>
      <EinstellungenNavigation aktiv={aktiv} />
      <div className="space-y-8">{children}</div>
    </div>
  );
}

/**
 * Die Erfolgsmeldung — ein stehender Platz, kein auftauchendes Element.
 *
 * Vorher gab sie `null` zurück, solange nichts gespeichert war. Beim ersten
 * Speichern schob sich dadurch ein neues Kind an den Anfang von `space-y-8`
 * und drückte den gesamten Inhalt darunter um eine Textzeile **plus** den
 * Abstand von 32px nach unten. Genau das sah man als Zucken: nicht die
 * Schrift flackerte, sondern die Seite sprang unter ihr weg.
 *
 * Jetzt steht der Platz immer. Erscheint eine Meldung, füllt sie ihn — es
 * bewegt sich nichts.
 *
 * Nebenbei richtig für Vorleseprogramme: Ein `role="status"`, das erst mit
 * seinem Text in den Baum kommt, wird oft nicht angesagt, weil der
 * Meldebereich zum Zeitpunkt der Änderung noch gar nicht bestand. Ein
 * bestehender Bereich mit `aria-live` wird zuverlässig vorgelesen.
 */
export function Erfolgsmeldung({ text }: { text: string | null }) {
  return (
    <p
      className="min-h-5 text-sm font-medium text-emerald-700 dark:text-emerald-400"
      role="status"
      aria-live="polite"
    >
      {text}
    </p>
  );
}

export function EinstellungsAbschnitt({
  titel,
  beschreibung,
  children,
  aktion,
}: {
  titel: string;
  beschreibung?: string;
  children: ReactNode;
  aktion?: ReactNode;
}) {
  return (
    <section className="max-w-3xl space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-linie pb-3">
        <div className="min-w-0 space-y-1">
          <h2 className="font-mono text-[12.5px] font-medium text-tinte">
            {titel}
          </h2>
          {beschreibung ? (
            <p className="max-w-[56ch] text-[13px] font-light leading-relaxed text-tinte3">
              {beschreibung}
            </p>
          ) : null}
        </div>
        {aktion ? <div className="shrink-0">{aktion}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function EinstellungsZeile({
  label,
  hinweis,
  children,
}: {
  label: string;
  hinweis?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-linie py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="min-w-0 space-y-1">
        <p className="text-[14px] text-tinte">{label}</p>
        {hinweis ? (
          <p className="max-w-[40ch] text-[12.5px] font-light leading-relaxed text-tinte3">
            {hinweis}
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-3">{children}</div>
    </div>
  );
}

export function meldungFuer(fehler: unknown, fallback: string): Meldung {
  return meldungZu(grundVon(fehler), {
    netzfehler: istNetzfehler(fehler),
    fallback: fehler instanceof Error ? fehler.message : fallback,
  });
}

export function LadenZeile({ text = "Lade …" }: { text?: string }) {
  return <p className="text-sm text-tinte3">{text}</p>;
}

export function FehlerBlock({
  fehler,
  fallback,
  onErneut,
}: {
  fehler: unknown;
  fallback: string;
  onErneut?: () => void;
}) {
  return (
    <Fehlerzeile meldung={meldungFuer(fehler, fallback)} onErneut={onErneut} />
  );
}

/** Häufige Zeitzonen für die Auswahl — plus Browser-Vorschlag. */
export const ZEITZONEN_AUSWAHL: readonly string[] = [
  "Europe/Berlin",
  "Europe/Vienna",
  "Europe/Zurich",
  "Europe/London",
  "Europe/Paris",
  "Europe/Amsterdam",
  "Europe/Warsaw",
  "Europe/Stockholm",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Kolkata",
  "Australia/Sydney",
  "Pacific/Auckland",
  "UTC",
];

export function zeitzonenMitBrowser(): string[] {
  const browser =
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : null;
  if (browser && !ZEITZONEN_AUSWAHL.includes(browser)) {
    return [browser, ...ZEITZONEN_AUSWAHL];
  }
  return [...ZEITZONEN_AUSWAHL];
}
